import { selectCurrentEntity } from '@opengov/app-core/entity';
import { IChartMargin } from '@opengov/components-visualization';
import { createSelector } from '@opengov/compose-app/reselect';
import { selectIsQueryRunning } from '@opengov/ogql-client/selectors';
import { BasicColumnTypes, IOgqlSemanticType, SemanticTypes } from '@opengov/ogql-types';
import {
  AmountType,
  AmountTypeCharacter,
  amountTypeValues,
  calculateColumnAmountType
} from '@opengov/reporting-formatters/formatters';
import { EEditMode } from '../../../src/types/MultiDatasetTypes';
import { applyCurrentMetadata } from '@opengov/reporting-transforms/dataMgrTransforms';
import { addCoAColsToLensCols, convertCoaColsToDimCols } from '@opengov/reporting-transforms/dimensionalColumns';
import { createTickLabelFormatters } from '@opengov/reporting-transforms/visualizations/axis';
import { mapLegendOptionsToSortOrder } from '@opengov/reporting-transforms/visualizations/chartCalculations';
import { createLineChartCalculations } from '@opengov/reporting-transforms/visualizations/createLineChartCalculations';
import { createPieChartCalculations } from '@opengov/reporting-transforms/visualizations/createPieChartCalculations';
import { createStackedBarChartCalculations } from '@opengov/reporting-transforms/visualizations/createStackedBarChartCalculations';
import {
  IChartConfigurator,
  ILineChartCalculations,
  IPieChartCalculations,
  IStackedBarChartCalculations
} from '@opengov/reporting-transforms/visualizations/types';
import { targetToAdditionalScaleData } from '@opengov/reporting-transforms/visualizations/values';
import {
  ColumnDefinition,
  EQueryType,
  EReportChartType,
  EReportTargetValueTypes,
  IReportConfiguration,
  IReportTarget,
  ReportControlType,
  ReportTableTabs,
  createColumnAlias
} from '@opengov/reporting-types';
import {
  EReportColumnTypes,
  IPercentOfTotalReportColumn,
  IReportColumn,
  getDisplayName,
  mapColumnToFactory
} from '@opengov/reporting-types/src/reportColumns-immer';
import isBlank from '@sedan-utils/is-blank';
import { some } from 'lodash';
import { AG_GRID_NEW_COLUMN_PLACEHOLDER_ID } from '../../../src/components/table/agGridConsts';
import { shouldHideColumnGroupHeaders } from '../../../src/components/table/gridUtils';
import { ROW_GROUP_INTERNAL_NAME, getRowGroupTreeDefs } from '../../../src/components/table/utils';
import { selectReportDataUpdateStatusDate } from '../../accountSummary/MultiDataset/selectors/selectReportDatasetLenses';
import { selectFiscalYearFiscalPeriodCombinedColumnFlag, selectNextGenGridCacheFlag } from '../../common/flags';
import { reportLocaleText, selectLocaleFormatter, selectReportExplorerMsgs } from '../../common/locale';
import {
  selectActiveReportTableTab,
  selectChartType,
  selectCurrentReportConfiguration,
  selectCustomColorMap,
  selectDetailTableFilter,
  selectHiddenControls,
  selectLegendSortOrder,
  selectRowGroups,
  selectShowColumn,
  selectShowColumnType,
  selectTarget
} from '../../common/reportConfiguration/selectors';
import { filterPredicateOfFYFPShowOptions } from '../../common/reportConfiguration/utils/fiscalYearFiscalPeriodUtils';
import { selectSummaryTableReportConfiguration } from '../../summaryTable/selectors';
import {
  getChartData,
  getContainerWidth,
  selectHasVisualizationData
} from '../ReportVisualization/selectors/chartSelectors';
import { chartSelector } from '../ReportVisualization/state';
import { reportExplorerSelector } from '../state';
import { DataTableStatus, VisualizationStatus } from '../types';
import { reportFormSelectors } from './organizedBy/selectors';
import {
  selectAccountTypeMapForCoaColumns,
  selectAllReportDatasetLenses,
  selectCoaDimensionColumns,
  selectReportCoaDimension,
  selectReportCoaDimensionId,
  selectReportCoaDimensionMetadata,
  selectReportCoaJoinColName,
  selectReportDatasetLens
} from './selectReportDimension';

// these magic numbers ensure that the x axis ticks do not overlap
// increase this to make "wider" ticks
const maxXTickWidth = 135;

const chartLeftMargin = 80;
const chartRightMargin = 40;
const chartWithTargetRightMargin = 90;
// these magic numbers ensure that the y axis ticks for stacked bar charts do not overlap
// increase this to make a larger left margin on horizontal stacked bar charts
const horizontalBarChartLeftMargin = 190;

const maxTargetXAxisLabelLength = 20;
const maxTargetYAxisLabelLength = 10;

function truncate(input: string, length: number): string {
  if (input.length > length) return `${input.substring(0, length).trim()}…`;
  else return input;
}

const identityFormatter = ({ value }) => value;

const columnToFormatter =
  ({ date, currency, percent, number: numF }) =>
  (columnType: IReportColumn['type']) => {
    if (!columnType) {
      return identityFormatter;
    }

    switch (columnType) {
      case BasicColumnTypes.Date:
      case BasicColumnTypes.Year:
        return date;
      case BasicColumnTypes.Currency:
        return currency;
      case BasicColumnTypes.Numeric:
        return numF;
      case BasicColumnTypes.Percent:
        return percent;
      default:
        return identityFormatter;
    }
  };

export const selectCurrentView = createSelector(reportExplorerSelector, (state) => state.currentView);

export const selectDimensionalTableQueryStatuses = createSelector(
  reportExplorerSelector,
  (state) => state.dimensionalTableQueryStatuses
);

export const selectCurrentSource = createSelector(
  selectReportDatasetLens,
  selectCurrentView,
  (datasetLens, currentView) => {
    return datasetLens || currentView;
  }
);

export const selectCurrentSourceName = createSelector(
  selectCurrentSource,
  (currentSource) => currentSource && currentSource.name
);

export const selectCurrentSourceUpdatedAt = createSelector(
  selectCurrentSource,
  selectReportDataUpdateStatusDate,
  (currentSource, dataUpdateStatusDate) => {
    return dataUpdateStatusDate || (currentSource && currentSource.dataUpdatedAt);
  }
);

export const selectLatestSourceUpdatedAt = createSelector(selectAllReportDatasetLenses, (allLenses) => {
  // Select all lenses and sort to find the latest
  if (Boolean(allLenses) && allLenses.length > 0) {
    if (allLenses.length === 1) {
      return allLenses[0].dataUpdatedAt;
    }
    const latestUpdatedAt = allLenses.sort((a, b) => {
      const updatedA = new Date(a.dataUpdatedAt);
      const updatedB = new Date(b.dataUpdatedAt);
      return (updatedA as any) - (updatedB as any);
    });
    return latestUpdatedAt[latestUpdatedAt.length - 1].dataUpdatedAt;
  }
  return;
});

export const selectCurrentSourceColumns = createSelector(selectCurrentSource, (source) => source && source.columns);

export const selectColumnAliasesFromDataMgr = createSelector(selectCurrentSourceColumns, (sourceColumns) => {
  const aliases = [];
  if (sourceColumns) {
    sourceColumns.forEach((column) => {
      aliases.push(
        createColumnAlias({
          originalName: column.name,
          displayName: column.displayName || column.label
        })
      );
    });
  }
  return aliases;
});

export const selectHiddenColumnsFromDataMgr = createSelector(selectCurrentSourceColumns, (sourceColumns) => {
  if (!sourceColumns) {
    return [];
  }
  const hiddenColumns = new Set<string>();
  sourceColumns.forEach((column) => {
    if (
      (column.metadata && column.metadata.tags && column.metadata.tags.hidden === true) ||
      column.hidden === true ||
      column.visible === false
    ) {
      hiddenColumns.add(column.name as string);
    }
  });

  return [...hiddenColumns];
});

const selectColumnsWithCoaAdded = createSelector(
  selectCurrentReportConfiguration,
  selectReportCoaDimensionMetadata,
  selectCoaDimensionColumns,
  selectReportCoaDimensionId,
  selectReportCoaJoinColName,
  selectAccountTypeMapForCoaColumns,
  (reportConfig, coaMetadata, coaColumns, coaDimensionId, joinColName, accountTypeMap) =>
    addCoAColsToLensCols({
      reportConfig,
      coaMetadata,
      coaColumns,
      coaDimensionId,
      joinColName,
      accountTypeMap
    })
);

const CAT_0_COLUMN_NAME = /segment_object_.._cat_0/;

// selectCoaDimensionColumns drops the cat_0 columns, so a saved filter on one has no column to bind to.
// They are hidden: the grid and the "organized by" controls skip them, but the query can still filter on them.
export const selectFilterReferencedCat0Columns = createSelector(
  selectDetailTableFilter,
  selectReportCoaDimension,
  selectReportCoaJoinColName,
  selectAccountTypeMapForCoaColumns,
  selectReportCoaDimensionId,
  (filters, coaDimension, joinColName, accountTypeMap, coaDimensionId) => {
    if (!joinColName || !coaDimensionId) return [];

    const hiddenCat0Columns = (coaDimension?.columns ?? [])
      .filter(({ name }) => CAT_0_COLUMN_NAME.test(name))
      .map((column) => ({ ...column, name: [joinColName, column.name].join(':'), visible: false }))
      .filter(({ name }) => some(filters, { columnName: name }));

    return convertCoaColsToDimCols(hiddenCat0Columns, joinColName, accountTypeMap, coaDimensionId);
  }
);

export const selectColumnsWithFilterReferencedCat0Added = createSelector(
  selectColumnsWithCoaAdded,
  selectFilterReferencedCat0Columns,
  (columns, cat0Columns) => {
    const existingColumnNames = new Set(columns.map(({ name }) => name));
    return columns.concat(cat0Columns.filter(({ name }) => !existingColumnNames.has(name)));
  }
);

export const selectReportConfigWithMetadata = createSelector(
  selectCurrentReportConfiguration,
  selectCurrentSourceColumns,
  selectColumnsWithFilterReferencedCat0Added,
  (reportConfig, dataMgrCols, columnsWithCoaAdded) => {
    if (!reportConfig) {
      return;
    }

    const isNotLensBased = reportConfig && !!reportConfig.viewId;
    return applyCurrentMetadata(reportConfig, dataMgrCols, !isNotLensBased, columnsWithCoaAdded);
  }
);

export const selectOrganizedByWithMetadata = createSelector(
  selectReportConfigWithMetadata,
  (config) => config && config.visualizationQuery && config.visualizationQuery.organizedBy
);

export const selectDetailTableColumns = createSelector(
  selectReportConfigWithMetadata,
  (reportConfig: IReportConfiguration): IReportColumn[] => {
    if (!reportConfig) return [];
    const presentationConfiguration = reportConfig?.presentationConfiguration?.columnNames;
    if (reportConfig && reportConfig.detailTableQuery && reportConfig.detailTableQuery.columns)
      return reportConfig.detailTableQuery.columns.map((c) => {
        const column = mapColumnToFactory(c);
        column.displayName = getDisplayName(c, presentationConfiguration);
        return column;
      });
  }
);

export const selectDetailTableColumnNamesMap = createSelector(selectReportConfigWithMetadata, (reportConfig) =>
  reportConfig?.detailTableQuery.columns.reduce((acc, col) => {
    const presConfig = reportConfig?.presentationConfiguration?.columnNames;
    acc[col.name] = getDisplayName(col, presConfig);
    return acc;
  }, {})
);

export const selectColumnDisplayName = createSelector(
  selectReportExplorerMsgs,
  selectReportConfigWithMetadata,
  (msgs, reportConfig) => (column) => {
    if (!reportConfig) {
      return null;
    }

    if (column.columnType === EReportColumnTypes.aggregate) {
      return msgs.controls[column.name] || msgs.controls.aggregate;
    }

    return getDisplayName(column, reportConfig.presentationConfiguration.columnNames);
  }
);

export const selectFirstPeriodColumn = createSelector(
  selectDetailTableColumns,
  (columns) =>
    columns &&
    columns.filter(
      (c) =>
        c.type === BasicColumnTypes.Period &&
        c.semanticTypes.some(
          (st) =>
            Object.prototype.hasOwnProperty.call(st, 'type') &&
            (st as IOgqlSemanticType).type === SemanticTypes.Period.FiscalMonth
        )
    )[0]
);

export const selectFirstPeriodColumnName = createSelector(selectFirstPeriodColumn, (column) => column && column.name);

export const selectColumnByNameFromCurrentView = createSelector(
  selectDetailTableColumns,
  (columns) => (columnName) => columns && columns.find((c) => c.name === columnName)
);

export const selectShowOptions = createSelector(
  selectDetailTableColumns,
  selectReportConfigWithMetadata,
  selectHiddenControls,
  selectHiddenColumnsFromDataMgr,
  selectShowColumn,
  selectFiscalYearFiscalPeriodCombinedColumnFlag,
  (columns, config, hidden, hiddenColsFromDataMgr, showColumn, canUseFYFPColumn) => {
    const showColumnAsPercentOfTotal = config && config.visualizationQuery.showColumnAsPercentOfTotal;
    if (!columns) {
      return {};
    }
    const optionCategories = columns
      .filter((c) => {
        return (
          amountTypeValues.includes(calculateColumnAmountType(c)) &&
          // we should respect the visibility as set in data manager --
          // but if a column that's currently used in column controls is
          // hidden in data manager, we still need to show it in the list
          // until the user selects something else.
          (!hiddenColsFromDataMgr.includes(c.name) || showColumn.name === c.name)
        );
      })
      .filter((c) => !hidden.find((h) => h.columnName === c.name && h.type === ReportControlType.Show))
      .filter((c) => !c.semanticTypes?.includes(SemanticTypes.Calendar.GregorianYear))
      .filter(filterPredicateOfFYFPShowOptions({ canUseFYFPColumn, showColumn }))
      .reduce((categories, option: any) => {
        const derivedType = calculateColumnAmountType(option);
        const amountType = showColumnAsPercentOfTotal ? AmountType.Percent : derivedType;
        if (categories[derivedType]) {
          const pushCategory = {
            label: option.displayName,
            value: option.name,
            secondaryLabel: AmountTypeCharacter[amountType]
          };
          categories[derivedType].push(pushCategory);
        } else {
          categories[derivedType] = [
            {
              label: option.displayName,
              value: option.name,
              secondaryLabel: AmountTypeCharacter[amountType]
            }
          ];
        }
        return categories;
      }, {});
    Object.keys(optionCategories).forEach(
      (cat) => (optionCategories[cat] = optionCategories[cat].sort((a, b) => (b.label > a.label ? -1 : 1)))
    );
    return optionCategories;
  }
);

export const selectChartTypeTab = createSelector(
  selectReportConfigWithMetadata,
  (state) => state.visualizationQuery.chartType
);

export const currentOrganizedBySelectors = reportFormSelectors(
  selectReportConfigWithMetadata,
  selectHiddenColumnsFromDataMgr
);

export const selectVisualizedReportConfiguration = createSelector(
  chartSelector,
  selectCurrentSourceColumns,
  (state, sourceColumns) => {
    const isNotLensBased = state.reportConfiguration && !!state.reportConfiguration.viewId;
    return applyCurrentMetadata(state.reportConfiguration, sourceColumns, !isNotLensBased);
  }
);

export const selectChartDetailTableColumns = createSelector(selectVisualizedReportConfiguration, (reportConfig) => {
  const presentationConfig =
    reportConfig && reportConfig.presentationConfiguration && reportConfig.presentationConfiguration.columnNames;
  if (reportConfig && reportConfig.detailTableQuery && reportConfig.detailTableQuery.columns)
    return reportConfig.detailTableQuery.columns.map((c) => {
      const column = mapColumnToFactory(c as any);
      column.displayName = getDisplayName(c as any, presentationConfig);
      return column;
    });
});

export const selectVisualizedIsPercentOfTotal = createSelector(
  selectVisualizedReportConfiguration,
  (state) => state && state.visualizationQuery.showColumnAsPercentOfTotal
);

export const selectVisualizedCategorizedByColumn = createSelector(
  selectVisualizedReportConfiguration,
  (state) => state && state.visualizationQuery?.categorizedByColumn
);

export const selectVisualizedChartType = createSelector(
  selectVisualizedReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.visualizationQuery.chartType
);

export const selectVisualizedShowColumn = createSelector(
  selectVisualizedReportConfiguration,
  (state) => state.visualizationQuery.showColumn
);

export const selectVisualizedPresentationConfiguration = createSelector(
  selectVisualizedReportConfiguration,
  (reportConfig) => reportConfig && reportConfig.presentationConfiguration
);
export const visualizedOrganizedBySelectors = reportFormSelectors(
  selectVisualizedReportConfiguration,
  selectHiddenColumnsFromDataMgr
);
export const summaryTableOrganizedBySelectors = reportFormSelectors(
  selectSummaryTableReportConfiguration,
  selectHiddenColumnsFromDataMgr
);

// Uses the visualizedReportConfig (report config for currently displayed visualization data)
// to generate the axis labels and formatting
export const selectLabels = createSelector(
  selectVisualizedReportConfiguration,
  selectCurrentEntity,
  reportFormSelectors(selectVisualizedReportConfiguration, selectHiddenColumnsFromDataMgr)
    .selectLabelForOrganizedByOption,
  reportLocaleText,
  (rc, entity, labelForOrgBy, text) => {
    const visualizationChartType = rc.visualizationQuery.chartType;
    const labels = createTickLabelFormatters({
      countString: text.reportExplorer.controls.count,
      emptyValues: text.reportExplorer.emptyValues,
      columnNames: rc.presentationConfiguration.columnNames,
      showColumn: rc.visualizationQuery.showColumn,
      organizedBy: rc.visualizationQuery.organizedBy,
      showColumnAsPercentOfTotal: rc.visualizationQuery.showColumnAsPercentOfTotal,
      visualizationChartType,
      organizedByColumn: rc.visualizationQuery.organizedBy.column,
      entityFiscalYearStartMonth: entity.fiscal_year_start_month
    });

    if (visualizationChartType === EReportChartType.HorizontalBar) {
      labels.yLabel = labelForOrgBy;
    } else {
      labels.xLabel = labelForOrgBy;
    }

    return labels;
  }
);

export const selectFiscalYearStartMonth = createSelector(
  selectCurrentEntity,
  (entity) => entity.fiscal_year_start_month
);

export const selectXTickLabelFormatter = createSelector(selectLabels, (details) => details.xTickLabelFormatter);

export const selectYTickLabelFormatter = createSelector(selectLabels, (details) => details.yTickLabelFormatter);

export const selectTooltipTickLabelFormatter = createSelector(
  selectChartType,
  selectXTickLabelFormatter,
  selectYTickLabelFormatter,
  (chartType, xTickLabelFormatter, yTickLabelFormatter) => {
    if (chartType === EReportChartType.HorizontalBar) {
      return yTickLabelFormatter;
    }
    return xTickLabelFormatter;
  }
);

export const selectChartCalculationProps = createSelector(
  getChartData,
  selectLegendSortOrder,
  getContainerWidth,
  (chartData, selectedSortOption, containerWidth): IChartConfigurator => {
    return {
      data: chartData,
      sortOrder: mapLegendOptionsToSortOrder(selectedSortOption),
      width: containerWidth
    };
  }
);

export const selectIsPercentOfTotal = createSelector(
  selectCurrentReportConfiguration,
  (config) => config && config.visualizationQuery.showColumnAsPercentOfTotal
);

export const selectExcludedCategorizedByValues = createSelector(
  selectReportConfigWithMetadata,
  (config) =>
    config && (config.visualizationQuery.showColumn as IPercentOfTotalReportColumn).excludedCategorizedByValues
);

export const selectHasTarget = createSelector(selectTarget, (target) => {
  return !!(target && target.elseStatus && target.conditions.length > 0);
});

export const selectMaxTargetAxisLabelLength = createSelector(selectVisualizedChartType, (chartType) => {
  if (chartType === EReportChartType.HorizontalBar) {
    return maxTargetXAxisLabelLength;
  }
  return maxTargetYAxisLabelLength;
});

export const selectTargetStatuses = createSelector(
  selectTarget,
  selectMaxTargetAxisLabelLength,
  (target, maxTargetAxisLabelLength) => {
    const targetStatuses = [];
    if (target) {
      const firstCondition = target.conditions[0];
      if (firstCondition) {
        targetStatuses.push({
          message: truncate(firstCondition.status.text, maxTargetAxisLabelLength),
          value: parseFloat(firstCondition.value.text.replace(/[$¢€,]+/g, '')),
          operator: firstCondition.operator
        });
        const lastCondition = target.conditions[target.conditions.length - 1];
        if (firstCondition !== lastCondition) {
          targetStatuses.push({
            message: truncate(lastCondition.status.text, maxTargetAxisLabelLength),
            value: parseFloat(lastCondition.value.text.replace(/[$¢€,]+/g, '')),
            operator: firstCondition.operator
          });
        }
      }
    }
    return targetStatuses;
  }
);

export const selectChartMargins = createSelector(
  selectVisualizedChartType,
  selectHasTarget,
  (chartType, hasTarget): IChartMargin => {
    switch (chartType) {
      case EReportChartType.Line:
      case EReportChartType.VerticalBar:
        return {
          top: 35,
          right: hasTarget ? chartWithTargetRightMargin : chartRightMargin,
          bottom: 60,
          left: chartLeftMargin
        };
      case EReportChartType.HorizontalBar:
        return {
          top: 50,
          right: chartRightMargin,
          bottom: 32,
          left: horizontalBarChartLeftMargin
        };
      default:
        return {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        };
    }
  }
);

export const createChartCalculations = ({
  chartType,
  data,
  sortOrder,
  width,
  isPercentOfTotal,
  margins,
  customColorMap,
  target
}) => {
  if (!data.length) {
    return {
      data: [],
      categories: []
    } as any;
  }

  const additionalScaleData = targetToAdditionalScaleData(target);

  const calcProps = { data, sortOrder, width };

  switch (chartType) {
    case EReportChartType.Line:
      return createLineChartCalculations(
        calcProps,
        isPercentOfTotal,
        margins,
        maxXTickWidth,
        customColorMap,
        additionalScaleData
      );
    case EReportChartType.VerticalBar:
      return createStackedBarChartCalculations({
        config: calcProps,
        showAsPercentOfTotal: isPercentOfTotal,
        orientation: 'vertical',
        margin: margins,
        maxXTickWidth,
        customColors: customColorMap,
        additionalScaleData
      });
    case EReportChartType.HorizontalBar:
      return createStackedBarChartCalculations({
        config: calcProps,
        showAsPercentOfTotal: isPercentOfTotal,
        orientation: 'horizontal',
        margin: margins,
        maxXTickWidth,
        customColors: customColorMap,
        additionalScaleData
      });
    case EReportChartType.Pie:
      return createPieChartCalculations(calcProps, isPercentOfTotal, margins, customColorMap);
    default:
      return;
  }
};

export const selectChartCalculations = createSelector(
  selectVisualizedChartType,
  selectChartCalculationProps,
  selectChartMargins,
  selectCustomColorMap,
  selectTarget,
  selectVisualizedIsPercentOfTotal,
  (
    chartType,
    calcProps,
    margins,
    customColorMap,
    target,
    isPercentOfTotal = false
  ): ILineChartCalculations | IStackedBarChartCalculations | IPieChartCalculations => {
    return createChartCalculations({
      chartType,
      data: calcProps.data,
      sortOrder: calcProps.sortOrder,
      width: calcProps.width,
      isPercentOfTotal,
      margins,
      customColorMap,
      target
    });
  }
);

export const selectChartCalculationsForExport = createSelector(
  selectVisualizedChartType,
  selectChartCalculationProps,
  selectChartMargins,
  selectCustomColorMap,
  selectTarget,
  selectVisualizedIsPercentOfTotal,
  (
    chartType,
    calcProps,
    margins,
    customColorMap,
    target,
    isPercentOfTotal = false
  ): ILineChartCalculations | IStackedBarChartCalculations | IPieChartCalculations => {
    return createChartCalculations({
      chartType,
      data: calcProps.data,
      sortOrder: calcProps.sortOrder,
      width: 1080,
      isPercentOfTotal,
      margins,
      customColorMap,
      target
    });
  }
);

export const selectNumberOfCategories = createSelector(selectChartCalculations, (calcs) => calcs.categories.length);

export const selectColorMap = createSelector(selectChartCalculations, (calcs) => calcs.colorMap);

export const selectVisualizationStatusFn = (haveData: boolean, queryRunning): VisualizationStatus => {
  if (!haveData) {
    return VisualizationStatus.Loading;
  }
  if (queryRunning(EQueryType.visualizationQuery)) {
    return VisualizationStatus.Updating;
  }
  return VisualizationStatus.Current;
};

export const selectVisualizationStatus = createSelector(
  selectHasVisualizationData,
  selectIsQueryRunning,
  (haveVisualizationData, queryRunning) => {
    return selectVisualizationStatusFn(haveVisualizationData, queryRunning);
  }
);

export const selectLabelsEnabled = createSelector(selectCurrentReportConfiguration, (config) => {
  const presConfig = config?.presentationConfiguration;
  if (!presConfig) {
    return false;
  }
  return Boolean(presConfig.labelsEnabled);
});

export const selectAxisTitlesEnabled = createSelector(selectCurrentReportConfiguration, (config) => {
  const presConfig = config?.presentationConfiguration;
  if (!presConfig) {
    return false;
  }
  return Boolean(config.presentationConfiguration?.axisTitlesEnabled);
});

export const selectDataTableStatus = createSelector(
  reportExplorerSelector,
  selectIsQueryRunning,
  selectActiveReportTableTab,
  (reportExplorerState, isQueryRunning, activeTab) => {
    // Details table starts in Loading and only transitions to Current when the SSRM getRows
    // fires on first mount. The Details grid is only mounted on the Details tab, so when the
    // Summary tab is active the raw Loading state would never clear. Skip the Loading guard
    // on Summary so the spinner reflects query activity rather than an unstarted Details fetch.
    if (reportExplorerState.dataTableStatus === DataTableStatus.Loading && activeTab !== ReportTableTabs.Summary) {
      return DataTableStatus.Loading;
    }

    const queryRunning =
      (isQueryRunning(EQueryType.visualizationQuery) && activeTab === ReportTableTabs.Summary) ||
      isQueryRunning(EQueryType.detailTableQuery) ||
      isQueryRunning(EQueryType.pivotColumn) ||
      isQueryRunning(EQueryType.pivotRows) ||
      isQueryRunning(EQueryType.pivotChildRows) ||
      isQueryRunning(EQueryType.detailTableCountQuery) ||
      (isQueryRunning(EQueryType.summaryTableQuery) && activeTab === ReportTableTabs.Summary);

    if (queryRunning) {
      return DataTableStatus.Updating;
    }

    return DataTableStatus.Current;
  }
);

export const selectIsDataTableStatusCurrent = createSelector(
  selectDataTableStatus,
  (tableStatus) => tableStatus === DataTableStatus.Current
);

export const selectCreateColumnEnabled = createSelector(
  selectDataTableStatus,
  selectVisualizationStatus,
  (tableStatus, vizStatus) => tableStatus === DataTableStatus.Current && vizStatus === VisualizationStatus.Current
);

export const selectPaginationOffset = createSelector(reportExplorerSelector, (state) => state.paginationOffset);

export const selectDataTableTotalRowCount = createSelector(
  reportExplorerSelector,
  (state) => state.dataTableTotalRowCount
);

export const selectReportTotalRowCount = createSelector(reportExplorerSelector, (state) => state.reportTotalRowCount);

export const selectColumnFormatter = createSelector(selectLocaleFormatter, columnToFormatter);

export const selectAboutCardMode = createSelector(reportExplorerSelector, (state) => state.settingsAboutCardMode);

export const selectCurrentViewExportable = createSelector(
  selectCurrentView,
  (currentView) =>
    currentView &&
    currentView.capabilities &&
    currentView.capabilities.export &&
    currentView.capabilities.export.exportable
);

export const selectCurrentViewMaxExportRows = createSelector(
  selectCurrentView,
  (currentView) =>
    currentView &&
    currentView.capabilities &&
    currentView.capabilities.export &&
    currentView.capabilities.export.maxExportRows
);

export const selectAgGridCacheBuster = createSelector(reportExplorerSelector, (state) => state.agGridCacheBuster);

export const selectAgGridParentData = createSelector(reportExplorerSelector, (state) => state.agGridParentData);

export const selectAgGridHasParentData = createSelector(
  selectAgGridParentData,
  selectNextGenGridCacheFlag,
  (agGridParentData, nextGenGridCacheFlag) => {
    if (agGridParentData === null || nextGenGridCacheFlag === null) return null;

    if (nextGenGridCacheFlag) {
      if (!agGridParentData?.length) return false;
      const parentDataIndex = agGridParentData[0].lastResolvedRowIndex.value;
      return agGridParentData?.[parentDataIndex]?.parentRows?.length > 0;
    }
    return agGridParentData.length > 0;
  }
);

export const selectDimensionalSummaryColumns = createSelector(
  reportExplorerSelector,
  (state) => state.dimensionalColumns
);

export const selectDimensionalReportConfiguration = createSelector(
  reportExplorerSelector,
  (state) => state.dimensionalReportConfiguration
);

export const selectDimensionalGroupData = createSelector(
  reportExplorerSelector,
  (state) => state.dimensionalGroupRowData
);

export const selectAllowedActions = createSelector(reportExplorerSelector, (state) => state.allowedActions);

export const selectFetchingPdfExportData = createSelector(
  reportExplorerSelector,
  (state) => state.fetchingPdfExportData
);
export const selectPdfExportData = createSelector(reportExplorerSelector, (state) => state.pdfExportData);
export const selectPdfExportTooMuchData = createSelector(reportExplorerSelector, (state) => state.pdfExportTooMuchData);
export const selectPivotColumns = createSelector(reportExplorerSelector, (state) => state.pivotColumns);
export const selectPivotColumnGroupIds = createSelector(reportExplorerSelector, (state) => state.pivotColumnGroupIds);
export const selectInEditMode = createSelector(reportExplorerSelector, (state) => state.editMode !== null);
export const selectInitialized = createSelector(reportExplorerSelector, (state) => state.initialized);
export const selectEditorDrawerOpen = createSelector(reportExplorerSelector, (state) => state.editorDrawerOpen);

export const selectEditMode = createSelector(reportExplorerSelector, (state) => state.editMode);

export const selectIsEditingColumn = createSelector(reportExplorerSelector, (state) =>
  [EEditMode.ColEdit, EEditMode.ColNew].includes(state.editMode)
);
export const selectIsEditingRow = createSelector(reportExplorerSelector, (state) =>
  [EEditMode.RowEdit, EEditMode.RowNew].includes(state.editMode)
);

export const selectIsEditModeDirty = createSelector(reportExplorerSelector, (state) => state.isEditModeDirty);

export const selectSelectedColumnGroupIndex = createSelector(
  reportExplorerSelector,
  (state) => state.selectedColumnGroupIndex
);

export const selectSelectedSegment = createSelector(reportExplorerSelector, (state) => state.selectedSegment);
export const selectSelectedColumnPath = createSelector(reportExplorerSelector, (state) => state.selectedColumnPath);
export const selectSelectedColumnLevel = createSelector(reportExplorerSelector, (state) => state.selectedColumnLevel);

export const selectSelectedSource = createSelector(reportExplorerSelector, (state) => state.selectedSource);

export const selectSelectedRowGroupIndex = createSelector(
  reportExplorerSelector,
  (state) => state.selectedRowGroupIndex
);

export const selectNewColumnGroupType = createSelector(reportExplorerSelector, (state) => state.newColumnGroupType);

export const selectNewRowGroupType = createSelector(reportExplorerSelector, (state) => state.newRowGroupType);

export const selectShowNewColumnPlaceHolder = createSelector(
  selectIsEditingColumn,
  selectNewColumnGroupType,
  selectSelectedColumnGroupIndex,
  (isEditingColumn, newColumnGroupType, selectedColumnGroupIndex) =>
    isEditingColumn && isBlank(selectedColumnGroupIndex) && !isBlank(newColumnGroupType)
);

export const selectShowNewRowPlaceHolder = createSelector(
  selectIsEditingRow,
  selectNewRowGroupType,
  selectSelectedRowGroupIndex,
  (isEditingRow, newRowGroupType, selectedRowGroupIndex) =>
    isEditingRow && isBlank(selectedRowGroupIndex) && !isBlank(newRowGroupType)
);

export const selectSelectedRowGroup = createSelector(reportExplorerSelector, (state) => state.selectedRowGroup);

export const selectIsRefreshRowData = createSelector(reportExplorerSelector, (state) => state.isRefreshRowData);
export const selectIsRefreshRowKeepingCache = createSelector(
  reportExplorerSelector,
  (state) => state.isRefreshRowKeepingCache
);

export const selectMdsAgGridColumns = createSelector(reportExplorerSelector, (state) => state.agGridColumns);

export const selectMultiGroupingDefinition = createSelector(selectRowGroups, getRowGroupTreeDefs);

export const selectCurrentColumnOrder = createSelector(
  selectCurrentReportConfiguration,
  (rc) => rc?.accountSummary?.columnOrder ?? {}
);

export const selectCurrentColumnsHidden = createSelector(selectCurrentReportConfiguration, (rc) => {
  return rc?.accountSummary?.hiddenColumns ?? {};
});

interface PlaceholderColumnDefinition {
  headerName: string;
  colId?: string;
  children?: { colId: string }[];
}

export const selectPlaceholderColumnDefinition = createSelector(
  selectCurrentReportConfiguration,
  reportLocaleText,
  selectInEditMode,
  (reportConfiguration, reportText, inEditMode): PlaceholderColumnDefinition => {
    const headerName = reportText.multidataset.columnEditorSection.defaultGroupName;
    const hideHeaders =
      shouldHideColumnGroupHeaders(reportConfiguration?.accountSummary?.columnGroups, inEditMode) ||
      (!inEditMode && !!reportConfiguration?.accountSummary?.hideColumnGroupHeaders);
    return hideHeaders
      ? { headerName, colId: AG_GRID_NEW_COLUMN_PLACEHOLDER_ID }
      : { headerName, children: [{ colId: AG_GRID_NEW_COLUMN_PLACEHOLDER_ID }] };
  }
);

export const selectColumnDefinitions = createSelector(
  selectMdsAgGridColumns,
  selectMultiGroupingDefinition,
  selectPlaceholderColumnDefinition,
  (columns, multigroupingDefinition, placeholderColumnDefinition): ColumnDefinition[] => {
    const colDefs: ColumnDefinition[] = [
      {
        field: `${ROW_GROUP_INTERNAL_NAME}_0`,
        hide: true,
        rowGroup: true
      },
      ...multigroupingDefinition,
      ...(columns ?? []),
      placeholderColumnDefinition
    ] as ColumnDefinition[];

    return colDefs.map((columnDef) => ({
      ...columnDef,
      suppressMovable: false,
      children: columnDef.children?.map((c) => ({ ...c, suppressMovable: false }))
    }));
  }
);

export const selectActiveNavigationPanelFilter = createSelector(
  reportExplorerSelector,
  (state) => state.activeNavigationPanelFilter
);

export const selectSelectedColumnEditorLensId = createSelector(
  reportExplorerSelector,
  (state) => state.selectedColumnEditorLensId
);

export const selectIsLoadingReportDatasetLenses = createSelector(
  reportExplorerSelector,
  (state) => state.isLoadingReportDatasetLenses
);

export function targetValueTypeIsSameAsShowColumnType(
  targetValueType: EReportTargetValueTypes,
  showColumnType: BasicColumnTypes
) {
  switch (targetValueType) {
    case EReportTargetValueTypes.currency:
      return showColumnType === BasicColumnTypes.Currency;
    case EReportTargetValueTypes.number:
      return showColumnType === BasicColumnTypes.Numeric;
    case EReportTargetValueTypes.percent:
      return showColumnType === BasicColumnTypes.Percent;
    default:
      return false;
  }
}

export function isTargetLinesValid(showColumnType: BasicColumnTypes, target: IReportTarget) {
  return target.conditions.every((condition) =>
    targetValueTypeIsSameAsShowColumnType(condition.value.valueType, showColumnType)
  );
}

export const selectIsTargetLinesValid = createSelector(selectShowColumnType, selectTarget, (showColumnType, target) => {
  if (showColumnType && target) {
    return isTargetLinesValid(showColumnType, target);
  }
  return false;
});
